import React from "react";

/**
 * CollectionArt — premium branded SVG placeholder artwork for the /shop gateway.
 *
 * Six scenes, one per collection. All artwork is original, uses only the
 * Gil Maakaroun soccer-themed palette (yellow / red / field green / gold /
 * ink / cream), and contains no Panini, FIFA, or third-party imagery.
 * Every scene is designed to be swapped out later for real product photography
 * by dropping an <img> in place of this component — the outer <div> keeps
 * the aspect ratio and rounded corners.
 */

const PALETTE = {
  yellow: "#FFCB05",
  red: "#E4002B",
  green: "#0B6E4F",
  gold: "#C9A55A",
  ink: "#0A0A0A",
  cream: "#FFF7E1",
  paper: "#FFFDF6",
  smoke: "#5B5B5B",
};

/** Diagonal stitched-pitch pattern behind every scene. */
function Pitch({ base = PALETTE.green, stripe = "rgba(255,255,255,0.05)" }) {
  return (
    <>
      <rect width="400" height="300" fill={base} />
      {[...Array(10)].map((_, i) => (
        <rect
          key={i}
          x={i * 40}
          y="0"
          width="20"
          height="300"
          fill={stripe}
        />
      ))}
      {/* subtle grain speckle */}
      {[...Array(24)].map((_, i) => (
        <circle
          key={`g${i}`}
          cx={(i * 53) % 400}
          cy={(i * 71) % 300}
          r="1"
          fill="rgba(255,255,255,0.08)"
        />
      ))}
    </>
  );
}

/* ------------------------------------------------------------- 1. Albums */
function AlbumScene() {
  return (
    <svg viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Collector album illustration">
      <Pitch base={PALETTE.ink} stripe="rgba(255,203,5,0.06)" />
      {/* album — front cover */}
      <g transform="translate(96 44)">
        <rect x="0" y="0" width="180" height="220" rx="10" fill={PALETTE.red} stroke={PALETTE.ink} strokeWidth="4" />
        <rect x="0" y="0" width="180" height="220" rx="10" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1" strokeDasharray="4 4" transform="translate(6 6)" />
        {/* wordmark bar */}
        <rect x="18" y="22" width="144" height="14" rx="3" fill={PALETTE.yellow} />
        {/* soccer ball motif */}
        <circle cx="90" cy="120" r="42" fill={PALETTE.paper} stroke={PALETTE.ink} strokeWidth="3" />
        <polygon points="90,94 108,108 101,131 79,131 72,108" fill={PALETTE.ink} />
        <path d="M90 78 L90 94 M114 100 L108 108 M66 100 L72 108 M78 148 L79 131 M102 148 L101 131" stroke={PALETTE.ink} strokeWidth="2" />
        {/* bottom stamp */}
        <rect x="30" y="180" width="120" height="20" rx="4" fill="rgba(255,255,255,0.14)" />
        <rect x="30" y="180" width="52" height="20" rx="4" fill={PALETTE.yellow} />
      </g>
      {/* corner stickers */}
      <g transform="translate(300 60) rotate(10)">
        <rect x="0" y="0" width="70" height="28" rx="14" fill={PALETTE.yellow} stroke={PALETTE.ink} strokeWidth="2" />
        <text x="35" y="19" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="11" fill={PALETTE.ink}>ALBUMS</text>
      </g>
      <g transform="translate(24 220) rotate(-4)">
        <rect x="0" y="0" width="86" height="30" rx="15" fill={PALETTE.paper} stroke={PALETTE.ink} strokeWidth="2" />
        <text x="43" y="20" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="12" fill={PALETTE.ink}>MATCHDAY</text>
      </g>
    </svg>
  );
}

/* -------------------------------------------------------- 2. Packs / Boxes */
function PacksScene() {
  return (
    <svg viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Sticker packs and box illustration">
      <Pitch base={PALETTE.red} stripe="rgba(255,255,255,0.07)" />
      {/* Box */}
      <g transform="translate(50 130)">
        <path d="M0 30 L150 60 L150 160 L0 130 Z" fill={PALETTE.ink} />
        <path d="M0 30 L80 0 L230 30 L150 60 Z" fill={PALETTE.yellow} stroke={PALETTE.ink} strokeWidth="3" />
        <path d="M150 60 L230 30 L230 130 L150 160 Z" fill={PALETTE.gold} stroke={PALETTE.ink} strokeWidth="3" />
        <path d="M0 30 L150 60 L150 160 L0 130 Z" fill="none" stroke={PALETTE.ink} strokeWidth="3" />
        <rect x="30" y="70" width="90" height="14" fill={PALETTE.red} stroke={PALETTE.ink} strokeWidth="2" />
      </g>
      {/* Foil pack 1 */}
      <g transform="translate(220 40) rotate(-10)">
        <rect x="0" y="0" width="90" height="130" rx="8" fill={PALETTE.yellow} stroke={PALETTE.ink} strokeWidth="3" />
        <rect x="6" y="6" width="78" height="16" fill={PALETTE.red} />
        <circle cx="45" cy="70" r="24" fill={PALETTE.paper} stroke={PALETTE.ink} strokeWidth="2" />
        <polygon points="45,54 55,62 51,75 39,75 35,62" fill={PALETTE.ink} />
        <rect x="14" y="104" width="62" height="12" fill={PALETTE.ink} />
        {/* torn top edge */}
        <path d="M0 0 L8 -4 L16 0 L24 -4 L32 0 L40 -4 L48 0 L56 -4 L64 0 L72 -4 L80 0 L88 -4" stroke={PALETTE.ink} strokeWidth="3" fill="none" />
      </g>
      {/* Foil pack 2 */}
      <g transform="translate(280 130) rotate(14)">
        <rect x="0" y="0" width="80" height="120" rx="8" fill={PALETTE.paper} stroke={PALETTE.ink} strokeWidth="3" />
        <rect x="6" y="6" width="68" height="14" fill={PALETTE.green} />
        <text x="40" y="70" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="30" fill={PALETTE.ink}>+5</text>
      </g>
      {/* sticker sparkle */}
      <g transform="translate(20 30) rotate(-8)">
        <rect x="0" y="0" width="86" height="28" rx="14" fill={PALETTE.paper} stroke={PALETTE.ink} strokeWidth="2" />
        <text x="43" y="19" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="11" fill={PALETTE.ink}>SEALED PACKS</text>
      </g>
    </svg>
  );
}

/* -------------------------------------------------------- 3. Gold Crumples */
function GoldScene() {
  return (
    <svg viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Gold foil chase piece illustration">
      <defs>
        <linearGradient id="foil-radial" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F6D97A" />
          <stop offset="45%" stopColor="#C9A55A" />
          <stop offset="70%" stopColor="#F1CD65" />
          <stop offset="100%" stopColor="#8C6E22" />
        </linearGradient>
      </defs>
      <Pitch base={PALETTE.ink} stripe="rgba(201,165,90,0.10)" />
      {/* central foil card */}
      <g transform="translate(120 40)">
        <rect x="0" y="0" width="160" height="220" rx="12" fill="url(#foil-radial)" stroke={PALETTE.ink} strokeWidth="4" />
        {/* crumple lines */}
        <path d="M10 40 L60 20 L110 55 L150 25" stroke="rgba(0,0,0,0.25)" strokeWidth="2" fill="none" />
        <path d="M8 90 L70 70 L110 105 L155 80" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" />
        <path d="M12 150 L60 130 L120 170 L150 140" stroke="rgba(0,0,0,0.20)" strokeWidth="2" fill="none" />
        <path d="M8 190 L80 175 L120 210" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" fill="none" />
        {/* stamp */}
        <rect x="24" y="88" width="112" height="44" rx="4" fill={PALETTE.ink} />
        <text x="80" y="118" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="20" fill={PALETTE.gold} letterSpacing="3">GOLD</text>
      </g>
      {/* Corner label */}
      <g transform="translate(24 32) rotate(-6)">
        <rect x="0" y="0" width="110" height="30" rx="15" fill={PALETTE.gold} stroke={PALETTE.ink} strokeWidth="2" />
        <text x="55" y="20" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="12" fill={PALETTE.ink}>CRUMPLE CHASE</text>
      </g>
    </svg>
  );
}

/* ------------------------------------------------------------ 4. Sets */
function SetsScene() {
  return (
    <svg viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Curated collector set illustration">
      <Pitch base={PALETTE.green} stripe="rgba(255,255,255,0.06)" />
      {/* 3x3 sticker grid */}
      {[0, 1, 2].map((r) =>
        [0, 1, 2].map((c) => {
          const fills = [
            [PALETTE.paper, PALETTE.yellow, PALETTE.paper],
            [PALETTE.yellow, PALETTE.paper, PALETTE.red],
            [PALETTE.paper, PALETTE.red, PALETTE.paper],
          ];
          return (
            <g key={`${r}${c}`} transform={`translate(${68 + c * 90} ${44 + r * 74})`}>
              <rect
                x="0"
                y="0"
                width="78"
                height="62"
                rx="4"
                fill={fills[r][c]}
                stroke={PALETTE.ink}
                strokeWidth="2.5"
              />
              <rect x="6" y="6" width="66" height="10" fill={PALETTE.ink} />
              <circle cx="39" cy="38" r="10" fill={PALETTE.ink} />
            </g>
          );
        })
      )}
      <g transform="translate(24 250) rotate(-4)">
        <rect x="0" y="0" width="112" height="30" rx="15" fill={PALETTE.yellow} stroke={PALETTE.ink} strokeWidth="2" />
        <text x="56" y="20" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="12" fill={PALETTE.ink}>CURATED SET</text>
      </g>
    </svg>
  );
}

/* --------------------------------------------------------- 5. Accessories */
function AccessoriesScene() {
  return (
    <svg viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Collector accessories illustration">
      <Pitch base={PALETTE.paper} stripe="rgba(10,10,10,0.04)" />
      {/* binder */}
      <g transform="translate(46 60)">
        <rect x="0" y="0" width="150" height="190" rx="6" fill={PALETTE.ink} />
        <rect x="14" y="0" width="4" height="190" fill={PALETTE.gold} />
        <rect x="22" y="0" width="4" height="190" fill={PALETTE.gold} />
        <rect x="34" y="24" width="100" height="16" fill={PALETTE.yellow} />
        <rect x="34" y="50" width="72" height="6" fill="rgba(255,255,255,0.4)" />
        <rect x="34" y="62" width="80" height="6" fill="rgba(255,255,255,0.3)" />
      </g>
      {/* sleeves stack */}
      <g transform="translate(212 90)">
        {[0, 1, 2, 3].map((i) => (
          <rect
            key={i}
            x={i * 8}
            y={i * 8}
            width="120"
            height="150"
            rx="6"
            fill={i === 3 ? PALETTE.red : "rgba(228,0,43,0.6)"}
            stroke={PALETTE.ink}
            strokeWidth="2"
          />
        ))}
        {/* window */}
        <rect x="34" y="34" width="94" height="120" rx="3" fill="rgba(255,255,255,0.18)" stroke={PALETTE.ink} strokeWidth="1.5" />
      </g>
      <g transform="translate(24 24) rotate(-4)">
        <rect x="0" y="0" width="106" height="30" rx="15" fill={PALETTE.ink} />
        <text x="53" y="20" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="12" fill={PALETTE.yellow}>ACCESSORIES</text>
      </g>
    </svg>
  );
}

/* ----------------------------------------------------------- 6. Merch */
function MerchScene() {
  return (
    <svg viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Collector merchandise illustration">
      <Pitch base={PALETTE.yellow} stripe="rgba(10,10,10,0.05)" />
      {/* jersey */}
      <g transform="translate(90 46)">
        <path
          d="M50 0 L20 20 L0 60 L28 74 L28 220 L172 220 L172 74 L200 60 L180 20 L150 0 L130 14 Q100 30 70 14 Z"
          fill={PALETTE.red}
          stroke={PALETTE.ink}
          strokeWidth="4"
        />
        {/* stripe */}
        <rect x="28" y="90" width="144" height="14" fill={PALETTE.ink} />
        <rect x="28" y="106" width="144" height="6" fill={PALETTE.paper} />
        {/* number */}
        <text x="100" y="176" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="66" fill={PALETTE.paper}>10</text>
      </g>
      <g transform="translate(24 232) rotate(-3)">
        <rect x="0" y="0" width="120" height="30" rx="15" fill={PALETTE.ink} />
        <text x="60" y="20" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="12" fill={PALETTE.yellow}>MERCH DROP</text>
      </g>
    </svg>
  );
}

const SCENES = {
  album: AlbumScene,
  packs: PacksScene,
  gold: GoldScene,
  sets: SetsScene,
  accessories: AccessoriesScene,
  merch: MerchScene,
};

export default function CollectionArt({ artKey, className = "" }) {
  const Scene = SCENES[artKey] || AlbumScene;
  return (
    <div className={`w-full aspect-[4/3] overflow-hidden ${className}`}>
      <Scene />
    </div>
  );
}
